import Breadcrumb, { BreadcrumbItem } from "components/Breadcrumb/Breadcrumb";
import Image from "next/legacy/image";
import { useEffect, useState } from "react";
import { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps } from "next";
import { wcApiDataService } from "functions/wcApi";
import { getSeoMetaTags } from "utils/common.utils";
import extractContentWooPost from "functions/extractContentWooPost";
import GlobalSeoMetaTags from "components/GlobalSeoMetaTags/GlobalSeoMetaTags";
import Head from "next/head";
import Link from "next/link";
type FrameShape = 'original' | 'capote';
type SizeKey = 'l' | 'm' | 's' | 'xs';

export default function FrameSizes({ post, metaDescription, seoMetaTags }: { post: any, metaDescription: string, seoMetaTags: any }) {
    const [selectedShape, setSelectedShape] = useState<FrameShape>('original');
    const [selectedSize, setSelectedSize] = useState('m');
    const [unit, setUnit] = useState<'mm' | 'inches'>('mm');
    const router = useRouter();

    useEffect(() => {
        if (router.locale == 'international') {
            setUnit('inches');
        }
    }, [router.locale]);

    const shapes = {
        original: {
            name: 'Original Frame',
            icon: 'https://w.dresden.vision/images/icons/shape_classic.png',
            sizes: ['xs', 's', 'm', 'l']
        },
        capote: {
            name: 'Capote Frame',
            icon: 'https://w.dresden.vision/images/icons/shape_round.png',
            sizes: ['s', 'm', 'l']
        }
    };

    const sizeLabels = {
        original: {
            xs: 'Extra Small (46 - 18.5 - 123)',
            s: 'Small (49.5 - 19 - 127)',
            m: 'Medium (53 - 19 - 135)',
            l: 'Large (55 - 20 - 142)'
        },
        capote: {
            s: 'Small (45.7 - 16.2 - 127)',
            m: 'Medium (47.8 - 16.2 - 135)',
            l: 'Large (50.7 - 16.2 - 142)'
        }
    };

    const frameMeasurements = {
        original: {
            l: { width: '144', lens: '55', bridge: '20', arm: '142' },
            m: { width: '137', lens: '53', bridge: '19', arm: '135' },
            s: { width: '130', lens: '49.5', bridge: '19', arm: '127' },
            xs: { width: '122.5', lens: '46', bridge: '18.5', arm: '123' }
        },
        capote: {
            l: { width: '141.7', lens: '50.7', bridge: '16.2', arm: '142' },
            m: { width: '136', lens: '47.8', bridge: '16.2', arm: '135' },
            s: { width: '130.2', lens: '45.7', bridge: '16.2', arm: '127' }
        }
    };

    const breadcrumbData: BreadcrumbItem[] = [
        {
            id: 1,
            href: '/',
            text: 'Dresden Vision'
        },
        {
            id: 2,
            href: '/our-glasses/frame-sizes',
            text: 'Frame Size'
        }
    ]

    const sizeOrder: Record<SizeKey, number> = { xs: 0, s: 1, m: 2, l: 3 };

    const imageAltTextByRegion = {
        "woman": {
            ca: "A woman in a beige jacket laughing outdoors while adjusting her Dresden Vision glasses, with modern architecture in the background",
            international: "Dynamic portrait of a woman wearing a Dresden Vision glasses and a light coat, smiling while moving outdoors near a modern building",
            nz: "Person wearing Dresden Vision glasses and a casual coat, sharing a genuine laugh outside a contemporary building",
            au: "Candid shot of a woman in a light jacket adjusting her Dresden Vision frames, smiling outside a modern structure"
        },
        "glasses": {
            ca: "Detailed eyewear sizing guide showing frame measurements and comparison chart for Dresden Vision glasses",
            international: "Technical diagram displaying standard eyewear measurements and size comparison table for Dresden Vision frames",
            nz: "Dresden Vision frame size guide with detailed measurements and comparison chart for choosing the right fit",
            au: "Comprehensive sizing diagram for Dresden Vision frames showing measurements and size comparison table"
        },
        "holding": {
            ca: "A hand holding modern blue-framed Dresden Vision prescription glasses against a light background, showing the frame's sleek design",
            international: "Close-up of contemporary Dresden Vision blue eyeglass frames held against a minimal white background",
            nz: "Simple blue Dresden Vision frames held up against a clean white backdrop, displaying the classic design",
            au: "Minimalist shot of Dresden Vision blue optical frames held up against a bright background"
        },
        "hug": {
            ca: "Two people wearing modern Dresden Vision eyeglasses share a warm embrace against an orange background, both smiling and wearing denim",
            international: "A close-up portrait of two people wearing Dresden Vision glasses, sharing a friendly moment against a warm orange backdrop",
            nz: "Two people wearing Dresden Vision frames embrace and smile, showcasing casual style against an orange background",
            au: "Two young people in casual wear showing off their trendy Dresden Vision specs, sharing a laugh against an orange backdrop"
        }
    };

    const getRegionBasedAltText = (tag: string) => {
        const locale = router.locale || 'international';
        return imageAltTextByRegion[tag as keyof typeof imageAltTextByRegion]?.[locale as keyof (typeof imageAltTextByRegion)['woman']] || '';
    };

    const pageTitle = post.title + ' | Dresden Vision';
    const isInternational = router.locale === 'international';
    const countryPageName = isInternational ? 'Dresden' : 'Dresden ' + router.locale?.toUpperCase();
    const publishDate = post.createdAt;
    const lastEditedTime = post.createdAt;

    const convertToInches = (mm: string): string => {
        const inches = parseFloat(mm) / 25.4;
        return (Math.floor(inches * 100) / 100).toFixed(2);
    };

    const formatMeasurement = (value: string): string => {
        return unit === 'mm' ? `${value}mm` : `${convertToInches(value)}"`;
    };

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta property="og:type" content="article" />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta name="description" content={metaDescription} />
                <meta property="og:site_name" content={countryPageName} />
                <meta property="article:published_time" content={publishDate} />
                <meta property="article:modified_time" content={lastEditedTime} />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:label1" content="Written by" />
                <meta name="twitter:data1" content="Dresden Vision" />
                <meta name="twitter:label2" content="Est. reading time" />
                <meta name="twitter:data2" content="1 minute" />
            </Head>
            <div className="w-full">
                <div className="relative hidden md:block h-[350px] w-full md:h-[400px] lg:h-[600px]">
                    <Image
                        src={`/images/frame_size_home.webp`}
                        alt="Frame Sizes"
                        layout="fill"
                        objectFit="cover"
                        priority
                    />
                </div>

                <div className="relative block md:hidden h-[360px] w-full">
                    <Image
                        src={`/images/framesizes-mobile.jpg`}
                        alt="Frame Sizes"
                        layout="fill"
                        objectFit="cover"
                        priority
                    />
                </div>

                <div className="mx-auto max-w-[1224px] px-4 font-sans leading-[1.7] text-brand-black75">
                    <div className="mt-[20px] mb-[30px]">
                        <Breadcrumb data={breadcrumbData as BreadcrumbItem[]} />
                    </div>

                    <div className="mt-8 space-y-16">
                        {/* Why does frame size matter? section */}
                        <div className="grid md:grid-cols-2 items-center bg-[#FFECD940] rounded-[36px]">
                            <div className="relative h-[300px] md:h-[480px] overflow-hidden rounded-t-[36px] md:rounded-t-[0px] md:rounded-l-[36px] md:rounded-tl-[36px]">
                                <Image
                                    src="/images/frame_size_woman.webp"
                                    layout="fill"
                                    objectFit="cover"
                                    priority
                                    alt={getRegionBasedAltText('woman')}
                                />
                            </div>
                            <div className="space-y-4 px-[20px] md:px-[60px] mt-[20px] md:mt-[0px] mb-[30px] md:mb-[0px]">
                                <h1 className="text-[24px] md:text-[40px] font-bold leading-[1.2]">
                                    Why does <br></br>frame size matter?
                                </h1>
                                <p className="text-[14px] md:text-[16px] text-[#2A2A2A]">
                                    A proper glasses fit is essential for both comfort and vision. If frames are too small, they&apos;ll pinch—if too big, they&apos;ll slip. Each lens has an optical center that needs to align directly over your pupil. Your vision works best when your eyes are centered in the lenses, which is why getting the right frame size is important.                            </p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 border border-[4px] border-[#F9F9F9] rounded-[36px] bg-[#fff]">

                            <div className="bg-[#F9F9F9] md:py-[50px] md:px-[100px] py-[36px] px-[24px]  rounded-l-[32px]">
                                <div>
                                    <p className="text-[24px] md:text-[42px] text-[#2A2A2A] font-bold">Fit & Sizing</p>
                                    <p className="text-[14px] md:text-[16px] text-[#2A2A2A] ">Please use this table as a guide to sizing.</p>
                                </div>

                                <div className="mt-[36px] mb-[36px]">
                                    <div className="flex items-center gap-2 mb-4 ">
                                        <span className={`text-sm ${unit === 'mm' ? 'text-[#2A2A2A]' : 'text-[#757575]'}`}>mm</span>
                                        <button
                                            onClick={() => setUnit(unit === 'mm' ? 'inches' : 'mm')}
                                            className="relative inline-flex h-6 w-11 items-center rounded-full bg-[#D9F3F7] h-[31px] w-[56px]"
                                            role="switch"
                                            aria-checked={unit === 'inches'}
                                        >
                                            <span
                                                className={`${unit === 'inches' ? 'translate-x-7 ' : 'translate-x-1 '
                                                    } inline-block h-[23px] w-[23px] transform rounded-full transition duration-200 ease-in-out shadow-sm bg-[#00AEC7]`}
                                            />
                                        </button>
                                        <span className={`text-sm ${unit === 'inches' ? 'text-[#2A2A2A]' : 'text-[#757575]'}`}>inches</span>
                                    </div>
                                </div>

                                <div className="space-y-4 mt-4">
                                    <h3 className="text-[14px] md:text-[16px] text-[#2A2A2A] font-semibold">Select Frame Shape</h3>
                                    {Object.entries(shapes).map(([key, shape]) => (
                                        <button
                                            key={key}
                                            onClick={() => {
                                                setSelectedShape(key as FrameShape);
                                                // Reset size to 'm' when switching shapes
                                                setSelectedSize('m');
                                            }}
                                            className={`w-full flex items-center gap-4 p-4 rounded-[6px] border-2 ${selectedShape === key ? 'border-[#FF8200]' : 'border-[#E6E6E6]'
                                                } bg-white max-w-[240px]`}
                                        >
                                            <div className="w-16 h-8">
                                                <Image
                                                    src={shape.icon}
                                                    alt={getRegionBasedAltText('glasses')}
                                                    width={64}
                                                    height={32}
                                                    objectFit="contain"
                                                />
                                            </div>
                                            <span className={`text-[14px] font-medium ${selectedShape === key ? '' : 'text-[#757575]'
                                                }`}>{shape.name}</span>
                                        </button>
                                    ))}
                                </div>

                                <div className="mt-[36px]">
                                    <p className="text-[14px] md:text-[14px] text-[#2A2A2A] font-semibold">Select Frame Size</p>

                                    <div className="mt-4 flex flex-wrap gap-[8px]">
                                        {shapes[selectedShape].sizes.map((size) => (
                                            <button
                                                key={size}
                                                onClick={() => setSelectedSize(size)}
                                                className={`w-full flex items-center justify-center gap-4 p-4 rounded-[6px] border-2 ${selectedSize === size ? 'border-[#FF8200]' : 'border-[#E6E6E6]'
                                                    } bg-white w-[54px] h-[50px]`}
                                            >
                                                <span className={`text-[14px] font-medium ${selectedSize === size ? '' : 'text-[#757575]'
                                                    }`}>{size.toUpperCase()}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>


                            <div className="flex flex-col items-center h-full w-full px-[10px] md:px-[42px]">

                                <div className="flex flex-col items-center justify-center mt-[50px]">
                                    <Image
                                        src={shapes[selectedShape].icon}
                                        alt={shapes[selectedShape].name}
                                        width={114}
                                        height={36}
                                        objectFit="contain"
                                    />
                                    <p className="text-[24px] font-bold">{shapes[selectedShape].name} Size</p>
                                    <div className="mt-4">
                                        <button className="flex items-center justify-center gap-4 bg-[#FF8200] text-white rounded-full text-center h-[40px] min-w-[250px]">
                                            <p className="text-[18px] font-medium">
                                                {(() => {
                                                    const label = sizeLabels[selectedShape][selectedSize as keyof typeof sizeLabels[typeof selectedShape]];
                                                    if (unit === 'mm') return label;

                                                    // Convert numbers to inches using the existing convertToInches function
                                                    return label.replace(/(\d+\.?\d*)/g, (match) =>
                                                        convertToInches(match)
                                                    );
                                                })()}
                                            </p>
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-[32px] bg-[#F9F9F9] rounded-[24px] px-[24px] py-[42px]  w-full h-full flex items-center justify-center">
                                    <Image
                                        src={`/images/frame_sizes/${unit === 'mm' ? 'mm' : 'inches'}/${selectedShape}_${selectedSize}.png`}
                                        alt={getRegionBasedAltText('glasses')}
                                        width={unit === 'mm' ? 450 : 550}
                                        height={unit === 'mm' ? 290 : 350}
                                        objectFit="contain"
                                    />
                                </div>

                                <div className="mt-[32px] mb-[52px]">
                                    <p className="md:text-[22px] text-[14px] text-[#2A2A2A] font-semibold text-center leading-[1.2]">Compare and match your measurements to our standard sizes below for the best fit</p>

                                    <div className="mt-3 w-full">
                                        <table className="w-full border-collapse">
                                            <thead>
                                                <tr className="text-left">
                                                    <th className="px-2 md:text-[16px] text-[12px] font-semibold">Size</th>
                                                    <th className="px-2 md:text-[16px] text-[12px] font-semibold">Lens Width</th>
                                                    <th className="px-2 md:text-[16px] text-[12px] font-semibold">Bridge Width</th>
                                                    <th className="px-2 md:text-[16px] text-[12px] font-semibold">Frame Width</th>
                                                    <th className="px-2 md:text-[16px] text-[12px] font-semibold">Arm Length</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(frameMeasurements[selectedShape as FrameShape])
                                                    .sort((a, b) => {
                                                        return sizeOrder[a[0] as SizeKey] - sizeOrder[b[0] as SizeKey];
                                                    })
                                                    .map(([size, measurements]) => (
                                                        <tr
                                                            key={size}
                                                            className={`md:text-[14px] text-[12px] ${selectedSize === size ? 'bg-[#FF8200] text-white' : ''
                                                                }`}
                                                        >
                                                            <td className="px-2 py-1 first:rounded-l-full">
                                                                {size === 'l' ? 'L' :
                                                                    size === 'm' ? 'M' :
                                                                        size === 's' ? 'S' : 'XS'}
                                                            </td>
                                                            <td className="px-2 py-1 ">{formatMeasurement(measurements.lens)}</td>
                                                            <td className="px-2 py-1 ">{formatMeasurement(measurements.bridge)}</td>
                                                            <td className="px-2 py-1 ">{formatMeasurement(measurements.width)}</td>
                                                            <td className="px-2 py-1 last:rounded-r-full">{formatMeasurement(measurements.arm)}</td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                        </div>

                        <div className="grid md:grid-cols-2 items-center bg-[#F1F3F7] rounded-[36px]">
                            <div className="order-2 md:order-1 space-y-4 px-[20px] md:px-[60px] mt-[20px] md:mt-[0px] mb-[30px] md:mb-[0px]">
                                <h1 className="text-[24px] md:text-[40px] font-bold leading-[1.2]">
                                    To find your current size check the temples of your current glasses
                                </h1>
                                <p className="text-[14px] md:text-[16px] text-[#2A2A2A]">
                                    Already wearing glasses or sunnies? On most frames, you will find universal measurements are often placed on the temples (arms) of your frames. Check these measurements on your current frames and compare them with the measurements in the table above. It&apos;s usually three numbers with a space between.
                                </p>
                            </div>
                            <div className="order-1 md:order-2 relative h-[300px] md:h-[480px] overflow-hidden rounded-r-[0px] md:rounded-r-[36px] rounded-t-[36px] md:rounded-t-[0px] rounded-tr-[36px]">
                                <Image
                                    src="/images/frame_size_glasses.webp"
                                    alt={getRegionBasedAltText('holding')}
                                    layout="fill"
                                    objectFit="cover"
                                    priority
                                />
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 items-center bg-[#FFECD940] rounded-[36px]">
                            <div className="relative h-[300px] md:h-[480px] overflow-hidden rounded-t-[36px] md:rounded-t-[0px] md:rounded-l-[36px]  md:rounded-tl-[36px]">
                                <Image
                                    src="/images/frame_size_hug.webp"
                                    alt={getRegionBasedAltText('hug')}
                                    layout="fill"
                                    objectFit="cover"
                                    priority
                                />
                            </div>
                            <div className="space-y-4 px-[20px] md:px-[60px] mt-[20px] md:mt-[0px] mb-[30px] md:mb-[0px]">
                                <h1 className="text-[24px] md:text-[40px] font-bold leading-[1.2]">
                                    First Pair of glasses?
                                </h1>
                                <p className="text-[14px] md:text-[16px] text-[#2A2A2A]">
                                    Print our size guide <Link href="https://cdn.dresden.vision/docs/dresden-frame-scaled-size-guide-2025.pdf" className="underline" target="_blank">here</Link> to compare our sizes, in your choice of frame shape. When printed at 100%, you can use these measurements to determine the best fit for your pupil distance and face width.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
            </div>
        </>
    );
}

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
    // Use hardcoded minimal post data instead of API call
    const post = {
        title: 'Frame Sizes',
        slug: 'frame-sizes',
        createdAt: '2021-08-17 11:29:34'
    };

    const metaDescription = '';//extractContentWooPost(post.htmlContent);
    const seoMetaTags = await getSeoMetaTags('/our-glasses/frame-sizes', locale);

    return {
        props: { post, metaDescription, seoMetaTags }
    };
};
